// UseFrameSizeStub.js - Web-compatible version of @react-navigation/elements useFrameSize
import * as React from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useSafeAreaFrame } from 'react-native-safe-area-context';
import useLatestCallback from 'use-latest-callback';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/with-selector';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";

// On web, SafeAreaListener might not exist, so we'll provide a fallback
let SafeAreaListener;
try {
  // Try to import it dynamically, but don't use require
  SafeAreaListener = null; // We'll handle this below
} catch (e) {
  SafeAreaListener = null;
}

const FrameContext = /*#__PURE__*/React.createContext(undefined);

export function useFrameSize(selector, throttle) {
  const context = React.useContext(FrameContext);
  if (context == null) {
    throw new Error('useFrameSize must be used within a FrameSizeProvider');
  }
  const value = useSyncExternalStoreWithSelector(
    throttle ? context.subscribeThrottled : context.subscribe,
    context.getCurrent,
    context.getCurrent,
    selector
  );
  return value;
}

export function FrameSizeProvider({ initialFrame, children }) {
  const context = React.useContext(FrameContext);
  if (context != null) {
    // If the context is already present, don't wrap again
    return children;
  }
  const frame = useSafeAreaFrame();
  const frameRef = React.useRef(initialFrame ?? frame);
  const [current] = React.useState(() => ({
    getCurrent() {
      return frameRef.current;
    }
  }));
  React.useEffect(() => {
    frameRef.current = frame;
  });
  
  // Simplified subscription logic for web
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  const subscriptions = React.useRef(new Set());
  const throttledSubscriptions = React.useRef(new Set());
  
  const subscribe = React.useCallback(callback => {
    subscriptions.current.add(callback);
    return () => {
      subscriptions.current.delete(callback);
    };
  }, []);
  
  const subscribeThrottled = React.useCallback(callback => {
    throttledSubscriptions.current.add(callback);
    return () => {
      throttledSubscriptions.current.delete(callback);
    };
  }, []);
  
  React.useEffect(() => {
    frameRef.current = frame;
    subscriptions.current.forEach(callback => callback());
    throttledSubscriptions.current.forEach(callback => callback());
  }, [frame]);
  
  const value = React.useMemo(() => ({
    ...current,
    subscribe,
    subscribeThrottled
  }), [current, subscribe, subscribeThrottled]);
  
  return _jsx(FrameContext.Provider, {
    value,
    children
  });
}

